/**
 * Activation flow state machine.
 *
 * Per UC22 (Comprehensive Road Test v3.0) + Tech Spec §6.1 + PFO §2.2,
 * activation walks the patient through:
 *
 *   1. Introduction + terms acknowledgment
 *   2. NIST IAL2 identity proofing through SHT-mediated vendor
 *      (or accommodation pathway per PFO §2.3 — accessibility,
 *      connectivity, language, documentation)
 *   3. PCI provisioning (substrate identity primitive issuance via
 *      pci-service)
 *   4. Initial Consent Artifact issuance (activation-scope artifact
 *      under the patient-rights authority frame per Tech Spec §4.2)
 *   5. Activation complete; patient projection becomes available
 *
 * The state machine encodes valid transitions between these phases
 * + their failure / accommodation branches. Linear forward flow with
 * step-back from intermediate steps; terminal states are
 * `complete` and `cancelled`.
 *
 * **Doctrinal posture.** Activation predates KEK release. KEK
 * provisioning + active session crypto wiring is claim-queue item
 * 2; this state machine surfaces no key material and makes no
 * crypto calls. The patient's PMK / KEK / DEK lifecycle does not
 * begin until the patient signs in (item 2) after activation has
 * completed (this item).
 *
 * **Accommodation framing.** PFO §2.3 names four accommodation
 * pathways: accessibility (screen reader, alternative inputs),
 * connectivity (offline / SMS / voice), language (preferred-language
 * coverage per SHT language-access policy), documentation (homeless
 * patterns, undocumented patient pathways, community-vouched, reduced-
 * evidence). The state machine surfaces accommodation-requested as
 * a first-class branch from identity-proofing rather than an error,
 * to avoid stigmatizing the path.
 */
/**
 * Terminal states are those from which the flow makes no further
 * transitions.
 */
export const TERMINAL_STATES = new Set([
    'complete',
    'cancelled',
    'error',
]);
export function isTerminal(state) {
    return TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
/**
 * Error thrown on invalid state transitions. Indicates an
 * orchestrator bug — production code should never produce this in
 * practice. Tests pin the state machine's invariants.
 */
export class ActivationTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid activation transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'ActivationTransitionError';
    }
}
/**
 * Apply an event to a state, returning the next state. Throws
 * `ActivationTransitionError` on invalid transitions.
 *
 * Cancellation + error events are accepted from any non-terminal
 * state (matching the user-cancellation + unrecoverable-error
 * pattern across the flow).
 */
export function applyEvent(state, event) {
    // Universal transitions from any non-terminal state.
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'intro':
            if (event.kind === 'acknowledge-terms') {
                return { kind: 'terms-acknowledged' };
            }
            break;
        case 'terms-acknowledged':
            if (event.kind === 'open-identity-proofing-session') {
                return {
                    kind: 'identity-proofing-pending',
                    sessionId: event.sessionId,
                    vendorRedirectUrl: event.vendorRedirectUrl,
                };
            }
            break;
        case 'identity-proofing-pending':
            if (event.kind === 'resolve-identity-proofing') {
                const r = event.result;
                if (r.outcome === 'verified') {
                    return { kind: 'identity-proofing-resolved', result: r };
                }
                if (r.outcome === 'rejected') {
                    return { kind: 'identity-proofing-rejected', result: r };
                }
                if (r.outcome === 'accommodation-requested' && r.accommodationKind !== undefined) {
                    return {
                        kind: 'accommodation-requested',
                        result: r,
                        accommodation: r.accommodationKind,
                    };
                }
                if (r.outcome === 'cancelled') {
                    return { kind: 'cancelled', reason: 'identity-proofing-cancelled' };
                }
                // Pending — stay in current state; orchestrator polls again.
                return state;
            }
            break;
        case 'identity-proofing-resolved':
            if (event.kind === 'continue-from-identity-proofing-resolved') {
                return {
                    kind: 'pci-provisioning-pending',
                    attestationToken: state.result.attestationToken,
                };
            }
            break;
        case 'pci-provisioning-pending':
            if (event.kind === 'pci-provisioned') {
                return { kind: 'pci-provisioned', pci: event.pci };
            }
            break;
        case 'pci-provisioned':
            // Brief render-only state. Orchestrator dispatches
            // `start-consent-artifact-issuance` to advance into the
            // pending state where the consent-artifact backend call
            // happens; the rendering layer can show a "your account
            // identity has been issued" confirmation while the next
            // call is in flight.
            if (event.kind === 'start-consent-artifact-issuance') {
                return { kind: 'consent-artifact-issuance-pending', pci: state.pci };
            }
            break;
        case 'consent-artifact-issuance-pending':
            if (event.kind === 'consent-artifact-issued') {
                return { kind: 'complete', pci: state.pci, artifact: event.artifact };
            }
            break;
        // Terminal + branch states have no forward transitions:
        case 'identity-proofing-rejected':
        case 'accommodation-requested':
        case 'complete':
        case 'cancelled':
        case 'error':
            break;
    }
    throw new ActivationTransitionError(state.kind, event.kind);
}
// ---------- Convenience constructors ----------
export function initialActivationState() {
    return { kind: 'intro' };
}
//# sourceMappingURL=activation-state.js.map